/** Linear-memory WASM module used as the LVGL preview framebuffer.
 *  A full LVGL Emscripten build can write the same RGBA buffer later;
 *  the editor paints label/button into this memory today. */
import { LvglRenderWidget, paintLvglWidgets, SCREEN_BG } from './lvglRender';

/** Minimal WASM: export `mem` as a growable linear memory (1 page = 64 KiB). */
function buildMemoryModule(pages: number): Uint8Array {
  // (module (memory (export "mem") <pages> <pages>))
  // Section ids: type=1 empty, memory=5, export=7
  const initial = Math.max(1, Math.min(pages, 0xffff));
  // memory section: count=1, flags=0 (no max), initial leb128
  const leb: number[] = [];
  let n = initial;
  do {
    let byte = n & 0x7f;
    n >>= 7;
    if (n) byte |= 0x80;
    leb.push(byte);
  } while (n);
  const memorySection = [0x05, 2 + leb.length, 0x01, 0x00, ...leb];
  // export "mem" → memory 0
  const exportSection = [
    0x07, 0x07, 0x01, 0x03, 0x6d, 0x65, 0x6d, 0x02, 0x00,
  ];
  return Uint8Array.from([
    0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00,
    ...memorySection,
    ...exportSection,
  ]);
}

let memory: WebAssembly.Memory | null = null;
let memoryPages = 0;

async function ensureMemory(bytes: number): Promise<WebAssembly.Memory> {
  const pages = Math.max(1, Math.ceil(bytes / 65536) + 1);
  if (memory && memoryPages >= pages) {
    return memory;
  }
  const module = await WebAssembly.instantiate(buildMemoryModule(pages));
  memory = module.instance.exports.mem as WebAssembly.Memory;
  memoryPages = pages;
  return memory;
}

export async function lvglFramebuffer(
  width: number,
  height: number,
  fillRgba: [number, number, number, number] = SCREEN_BG,
): Promise<ImageData | null> {
  const w = Math.floor(width);
  const h = Math.floor(height);
  const bytes = Math.max(0, w * h * 4);
  if (bytes <= 0) {
    return null;
  }
  try {
    const mem = await ensureMemory(bytes);
    const view = new Uint8ClampedArray(mem.buffer, 0, bytes);
    const [r, g, b, a] = fillRgba;
    for (let i = 0; i < bytes; i += 4) {
      view[i] = r;
      view[i + 1] = g;
      view[i + 2] = b;
      view[i + 3] = a;
    }
    return new ImageData(view.slice(0, bytes), w, h);
  } catch {
    return null;
  }
}

/** Paint layout widgets into WASM memory and return a copy as ImageData. */
export async function lvglRenderFrame(
  width: number,
  height: number,
  widgets: LvglRenderWidget[],
  selectedId?: string,
): Promise<ImageData | null> {
  const w = Math.floor(width);
  const h = Math.floor(height);
  const bytes = Math.max(0, w * h * 4);
  if (bytes <= 0) {
    return null;
  }
  try {
    const mem = await ensureMemory(bytes);
    const view = new Uint8ClampedArray(mem.buffer, 0, bytes);
    paintLvglWidgets(view, w, h, widgets, { selectedId });
    return new ImageData(view.slice(0, bytes), w, h);
  } catch {
    return null;
  }
}
